"use client";

import type { ReactNode } from "react";

export type SortableColumn =
    | "exerciseName"
    | "status"
    | "subject"
    | "topic"
    | "questionType"
    | "gameType"
    | "createdDate";

export type SortDirection = "asc" | "desc";

export type LibrarySortState = {
    column: SortableColumn | null;
    direction: SortDirection;
};

export const DEFAULT_LIBRARY_SORT_STATE: LibrarySortState = {
    column: null,
    direction: "asc",
};

export type LibrarySortableRow = {
    exerciseName: string;
    title: string;
    status: boolean;
    subject: string;
    topic: string;
    rawQuestionType: string | null;
    gameType: string;
    createdAtRaw: string;
};

const CELL_TRUNCATE = "min-w-0 truncate whitespace-nowrap";
const CELL_NOWRAP = "whitespace-nowrap";

function compareText(a: string, b: string) {
    return a.localeCompare(b, "th", { sensitivity: "base" });
}

function getExerciseName(row: LibrarySortableRow) {
    const name = String(row.exerciseName ?? row.title ?? "").trim();
    return name || "-";
}

function isSingleQuestion(row: LibrarySortableRow) {
    return row.rawQuestionType === "single_question";
}

function getCreatedAtTime(row: LibrarySortableRow) {
    const time = new Date(row.createdAtRaw).getTime();
    return Number.isFinite(time) ? time : 0;
}

export function toggleLibrarySort(
    column: SortableColumn,
    current: LibrarySortState,
): LibrarySortState {
    if (current.column !== column) {
        return { column, direction: "asc" };
    }

    return {
        column,
        direction: current.direction === "asc" ? "desc" : "asc",
    };
}

export function sortLibraryRows<T extends LibrarySortableRow>(
    rows: T[],
    sortState: LibrarySortState,
): T[] {
    if (!sortState.column) {
        return rows;
    }

    const sorted = [...rows];
    const { column, direction } = sortState;
    const multiplier = direction === "asc" ? 1 : -1;

    sorted.sort((a, b) => {
        switch (column) {
            case "exerciseName":
                return compareText(getExerciseName(a), getExerciseName(b)) * multiplier;
            case "status": {
                const diff = Number(b.status) - Number(a.status);
                return diff * multiplier;
            }
            case "subject":
                return compareText(a.subject, b.subject) * multiplier;
            case "topic":
                return compareText(a.topic, b.topic) * multiplier;
            case "questionType": {
                const diff = Number(isSingleQuestion(b)) - Number(isSingleQuestion(a));
                return diff * multiplier;
            }
            case "gameType":
                return compareText(a.gameType, b.gameType) * multiplier;
            case "createdDate": {
                const diff = getCreatedAtTime(a) - getCreatedAtTime(b);
                return diff * multiplier;
            }
            default:
                return 0;
        }
    });

    return sorted;
}

type SortableHeaderCellProps = {
    label: string;
    column: SortableColumn;
    sortState: LibrarySortState;
    onSort: (column: SortableColumn) => void;
    className?: string;
    align?: "left" | "center";
};

function SortableHeaderCell({
    label,
    column,
    sortState,
    onSort,
    className = "",
    align = "left",
}: SortableHeaderCellProps) {
    const isActive = sortState.column === column;

    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            className={`cursor-pointer font-semibold transition hover:text-[#0092D7] ${align === "center" ? "text-center" : "text-left"} ${isActive ? "text-[#0092D7]" : "text-[#4E4E4E]"} ${className}`}
        >
            {label}
            {isActive ? (
                <span className="ml-0.5 text-xs">{sortState.direction === "asc" ? "↑" : "↓"}</span>
            ) : null}
        </button>
    );
}

type LibrarySortableHeaderProps = {
    showStatusColumn: boolean;
    sortState: LibrarySortState;
    onSortChange: (next: LibrarySortState) => void;
    labels: {
        exerciseName: string;
        status: string;
        subject: string;
        topic: string;
        questionType: string;
        gameType: string;
        createdDate: string;
        preview: string;
    };
    viewControls: ReactNode;
};

export default function LibrarySortableHeader({
    showStatusColumn,
    sortState,
    onSortChange,
    labels,
    viewControls,
}: LibrarySortableHeaderProps) {
    const handleSort = (column: SortableColumn) => {
        onSortChange(toggleLibrarySort(column, sortState));
    };

    return (
        <div className="col-span-full grid grid-cols-subgrid items-center gap-x-4 gap-y-3 px-6 py-3 text-md font-semibold text-[#4E4E4E]">
            <span />
            <SortableHeaderCell
                label={labels.exerciseName}
                column="exerciseName"
                sortState={sortState}
                onSort={handleSort}
                className={CELL_TRUNCATE}
            />
            <span />
            {showStatusColumn ? (
                <SortableHeaderCell
                    label={labels.status}
                    column="status"
                    sortState={sortState}
                    onSort={handleSort}
                    className="whitespace-nowrap"
                    align="center"
                />
            ) : null}
            <SortableHeaderCell
                label={labels.subject}
                column="subject"
                sortState={sortState}
                onSort={handleSort}
                className={CELL_TRUNCATE}
            />
            <SortableHeaderCell
                label={labels.topic}
                column="topic"
                sortState={sortState}
                onSort={handleSort}
                className={CELL_TRUNCATE}
            />
            <SortableHeaderCell
                label={labels.questionType}
                column="questionType"
                sortState={sortState}
                onSort={handleSort}
                className={CELL_TRUNCATE}
            />
            <SortableHeaderCell
                label={labels.gameType}
                column="gameType"
                sortState={sortState}
                onSort={handleSort}
                className={CELL_NOWRAP}
            />
            <SortableHeaderCell
                label={labels.createdDate}
                column="createdDate"
                sortState={sortState}
                onSort={handleSort}
                className={CELL_TRUNCATE}
            />
            <span className="text-center whitespace-nowrap">{labels.preview}</span>
            <div className="flex items-center justify-end gap-1">{viewControls}</div>
        </div>
    );
}
